import dayjs from "dayjs";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

// The width the page is laid out at while it is captured. Fixed on purpose: the export
// must be the same document whether it was taken from a laptop or a phone. 900px is the
// desktop side of the app's breakpoints and close to the reports' own max-width, so the
// capture keeps their designed proportions and lands at a readable size on the sheet — the
// wider the capture, the smaller everything gets once it is scaled to the sheet.
const DEFAULT_RENDER_WIDTH_PX = 900;
// Breathing room around the captured page, in the PDF.
const PAGE_PADDING_PX = 32;

// US Letter, not A4: these reports are read and printed by US authorities (ANSI/ASME
// Y14.1), and Austin's Building Criteria Manual puts 8.5x11 as the smallest sheet it
// accepts. A4 would come out of a US tray scaled or clipped.
const LETTER_WIDTH_PT = 612;
const LETTER_HEIGHT_PT = 792;
const PAGE_MARGIN_PT = 18;
// Two device pixels per CSS pixel: ~300 dpi at page width, without a huge file.
const CAPTURE_SCALE = 2;
// Anything shorter than this is treated as one piece a page break must not cut through:
// rows, headings, badges, short paragraphs. Taller blocks may be split.
const MAX_ATOMIC_HEIGHT_PX = 140;

/**
 * File name for an exported report: the parts that make it identifiable in a folder full
 * of them (property, record ids) plus the moment it was exported.
 */
export const buildReportFileName = (...parts: (string | number | null | undefined)[]): string =>
	[...parts, dayjs().format("YYYY-MM-DD_HH-mm-ss")]
		.filter(part => part !== null && part !== undefined && String(part).trim() !== "")
		.map(part =>
			String(part)
				.trim()
				.replace(/[/\\?%*:|"<>]/g, "-")
				.replace(/\s+/g, " ")
		)
		.join(" - ");

/** Resolves once the fonts and every image inside the node are ready to be painted. */
const waitForAssets = async (node: HTMLElement): Promise<void> => {
	const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
	const images = Array.from(node.querySelectorAll("img"));
	await Promise.all([
		fonts?.ready ?? Promise.resolve(),
		...images.map(image =>
			image.complete
				? Promise.resolve()
				: new Promise<void>(resolve => {
						image.addEventListener("load", () => resolve(), { once: true });
						image.addEventListener("error", () => resolve(), { once: true });
					})
		)
	]);
};

/**
 * Y positions (in canvas pixels) a page break must not land on: inside any element small
 * enough to be kept whole. Derived from the laid-out DOM, so it needs no knowledge of what
 * the page is made of.
 */
const collectAtomicBands = (root: HTMLElement, ratio: number): { top: number; bottom: number }[] => {
	const rootTop = root.getBoundingClientRect().top;
	const bands: { top: number; bottom: number }[] = [];
	root.querySelectorAll<HTMLElement>("*").forEach(element => {
		const rect = element.getBoundingClientRect();
		if (rect.height <= 0 || rect.height > MAX_ATOMIC_HEIGHT_PX) return;
		bands.push({ top: (rect.top - rootTop) * ratio, bottom: (rect.bottom - rootTop) * ratio });
	});
	return bands;
};

/** Highest cut at or below `limit` that doesn't slice through one of those bands. */
const findCut = (bands: { top: number; bottom: number }[], from: number, limit: number): number => {
	let cut = limit;
	// Moving the cut above the band it crosses can land it inside an earlier one, so this
	// repeats until it settles — or gives up and cuts at the limit rather than loop.
	for (let pass = 0; pass < 12; pass++) {
		let highest = Infinity;
		for (const band of bands) {
			if (band.top < cut && cut < band.bottom) highest = Math.min(highest, band.top);
		}
		if (highest === Infinity) return cut;
		if (highest <= from) return limit;
		cut = highest;
	}
	return cut > from ? cut : limit;
};

/** Copies one horizontal band of the capture onto its own canvas, to add as a page. */
const sliceCanvas = (source: HTMLCanvasElement, top: number, height: number): HTMLCanvasElement => {
	const slice = document.createElement("canvas");
	slice.width = source.width;
	slice.height = height;
	const context = slice.getContext("2d");
	if (context) {
		context.fillStyle = "#ffffff";
		context.fillRect(0, 0, slice.width, slice.height);
		context.drawImage(source, 0, top, source.width, height, 0, 0, source.width, height);
	}
	return slice;
};

/**
 * Renders an element to a multi-page US Letter PDF by photographing the real thing: the element
 * is cloned into an off-screen container of a fixed width, laid out by the browser with
 * the app's own stylesheet, captured, and sliced into pages.
 *
 * The clone is what makes the export stop depending on the window: the layout is always
 * the one for `renderWidthPx` and the height is whatever the content needs — a taller
 * report is simply more pages. Because it is the same DOM and the same CSS the user
 * is looking at, changing the page changes the PDF; there is no second layout to maintain.
 *
 * Anything marked `data-pdf-hide` (buttons, navigation) is dropped from the copy.
 */
export const renderElementToPdf = async (
	element: HTMLElement,
	renderWidthPx: number = DEFAULT_RENDER_WIDTH_PX
): Promise<jsPDF> => {
	// Off-screen rather than hidden: `display: none` has no layout, and the clone has to be
	// measured. It stays in the same document, so it inherits the app's stylesheet.
	const stage = document.createElement("div");
	stage.setAttribute("aria-hidden", "true");
	stage.style.cssText = [
		"position: fixed",
		"top: 0",
		`left: -${renderWidthPx + 200}px`,
		`width: ${renderWidthPx}px`,
		`padding: ${PAGE_PADDING_PX}px`,
		"background: #ffffff",
		"pointer-events: none",
		"z-index: -1"
	].join(";");

	const clone = element.cloneNode(true) as HTMLElement;
	clone.style.width = "100%";
	clone.style.maxWidth = "none";
	clone.querySelectorAll("[data-pdf-hide]").forEach(hidden => hidden.remove());
	stage.appendChild(clone);
	document.body.appendChild(stage);

	try {
		await waitForAssets(stage);

		const stageWidth = renderWidthPx + PAGE_PADDING_PX * 2;
		const stageHeight = stage.scrollHeight;
		const canvas = await html2canvas(stage, {
			scale: CAPTURE_SCALE,
			backgroundColor: "#ffffff",
			useCORS: true,
			logging: false,
			// The clone is re-cloned into an iframe of exactly this size. Without the height
			// the iframe keeps the viewport's, and the capture ends where the screen would —
			// which is what makes an export depend on the window it was taken from.
			windowWidth: stageWidth,
			windowHeight: stageHeight,
			width: stageWidth,
			height: stageHeight,
			scrollX: 0,
			scrollY: 0
		});

		const pdf = new jsPDF({ unit: "pt", format: "letter" });
		const contentWidthPt = LETTER_WIDTH_PT - PAGE_MARGIN_PT * 2;
		const contentHeightPt = LETTER_HEIGHT_PT - PAGE_MARGIN_PT * 2;
		// Canvas pixels per point, so a page height can be expressed in canvas pixels.
		const pxPerPt = canvas.width / contentWidthPt;
		const pageHeightPx = contentHeightPt * pxPerPt;
		const bands = collectAtomicBands(stage, canvas.height / stageHeight);

		let top = 0;
		let isFirstPage = true;
		while (top < canvas.height - 1) {
			const limit = Math.min(top + pageHeightPx, canvas.height);
			const bottom = limit >= canvas.height ? canvas.height : findCut(bands, top, limit);
			const sliceHeight = Math.max(1, Math.round(bottom - top));

			if (!isFirstPage) pdf.addPage();
			pdf.addImage(
				sliceCanvas(canvas, top, sliceHeight).toDataURL("image/jpeg", 0.9),
				"JPEG",
				PAGE_MARGIN_PT,
				PAGE_MARGIN_PT,
				contentWidthPt,
				sliceHeight / pxPerPt
			);

			isFirstPage = false;
			top = bottom;
		}

		return pdf;
	} finally {
		stage.remove();
	}
};

/** Renders the element and downloads the result. */
export const exportElementToPdf = async (
	element: HTMLElement,
	fileName: string,
	renderWidthPx?: number
): Promise<void> => {
	const pdf = await renderElementToPdf(element, renderWidthPx);
	pdf.save(`${fileName}.pdf`);
};
